import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Copy,
  FileDown,
} from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { vendorAdvanceService } from '@/services/vendorAdvanceService'
import { billsService } from '@/services/billsService'
import { VendorAdvance, BillVendor } from '@/api/types'

export const VendorAdvancesList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [advances, setAdvances] = useState<VendorAdvance[]>([])
  const [vendors, setVendors] = useState<BillVendor[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedVendorId, setSelectedVendorId] = useState<number | undefined>(undefined)

  const branchId = user?.selectedBranch?.branchId

  // Load vendors for filter
  useEffect(() => {
    if (!branchId) return
    const loadVendors = async () => {
      try {
        const vendorList = await billsService.getAllVendors(branchId)
        setVendors(vendorList)
      } catch (error: any) {
        // Error handled silently
      }
    }
    loadVendors()
  }, [branchId])

  // Load advances
  useEffect(() => {
    if (!branchId) {
      return
    }

    const loadAdvances = async () => {
      try {
        setLoading(true)
        const response = await vendorAdvanceService.getAllAdvances(branchId, pageNumber, pageSize)
        const list = response.data || []
        setAdvances(list)
        setTotalCount(response.totalRecords || list.length || 0)
        const pages = response.totalRecords ? Math.ceil(response.totalRecords / response.pageSize) : 0
        setTotalPages(pages || 1)
      } catch (error: any) {
        const status = error.response?.status
        const msg = (error.response?.data?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setAdvances([])
        setTotalCount(0)
        setTotalPages(1)
        if (!isNoData) {
          toast.error(error.response?.data?.message || 'Failed to load advances')
        }
      } finally {
        setLoading(false)
      }
    }

    loadAdvances()
  }, [branchId, pageNumber, pageSize])

  const filteredAdvances = useMemo(() => {
    return advances.filter((advance) => {
      if (selectedVendorId && advance.vendorId !== selectedVendorId) {
        return false
      }

      if (!searchQuery.trim()) return true
      const query = searchQuery.toLowerCase()
      return (
        (advance.advNo || '').toLowerCase().includes(query) ||
        (advance.vendorName || '').toLowerCase().includes(query) ||
        (advance.billRef || '').toLowerCase().includes(query) ||
        (advance.paymentRef || '').toLowerCase().includes(query) ||
        (advance.refNo || '').toLowerCase().includes(query)
      )
    })
  }, [advances, selectedVendorId, searchQuery])

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const csvEscape = (cell: string | number) => `"${String(cell).replace(/"/g, '""')}"`

  const handleCopy = async () => {
    if (filteredAdvances.length === 0) {
      toast.error(t('vendorAdvances.nothingToCopy', { defaultValue: 'Nothing to copy.' }))
      return
    }
    const header = ['Adv No', 'Vendor', 'Bill Ref', 'Payment Ref', 'Date', 'Amount'].join('\t')
    const lines = filteredAdvances.map((a) =>
      [
        a.advNo ?? '',
        a.vendorName ?? '',
        a.billRef ?? '',
        a.paymentRef ?? a.refNo ?? '',
        formatDate(a.advDate as any),
        String(a.amount ?? ''),
      ].join('\t')
    )
    await navigator.clipboard.writeText([header, ...lines].join('\n'))
    toast.success(t('vendorAdvances.copiedToClipboard', { defaultValue: 'Copied to clipboard.' }))
  }

  const handleCsv = () => {
    if (filteredAdvances.length === 0) {
      toast.error(t('vendorAdvances.nothingToExport', { defaultValue: 'Nothing to export.' }))
      return
    }
    const header = ['Adv No', 'Vendor', 'Bill Ref', 'Payment Ref', 'Date', 'Amount'].map(csvEscape).join(',')
    const body = filteredAdvances
      .map((a) =>
        [
          a.advNo ?? '',
          a.vendorName ?? '',
          a.billRef ?? '',
          a.paymentRef ?? a.refNo ?? '',
          formatDate(a.advDate as any),
          String(a.amount ?? ''),
        ]
          .map(csvEscape)
          .join(',')
      )
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `vendor_advances_${new Date().toISOString().slice(0, 10)}.csv`
    link.click()
    window.URL.revokeObjectURL(url)
    toast.success(t('vendorAdvances.csvDownloaded', { defaultValue: 'CSV downloaded.' }))
  }

  const handleAddAdvance = () => {
    navigate('/purchases/vendor-advances/new')
  }

  const handleEditAdvance = (advance: VendorAdvance) => {
    const advanceId = advance.id
    if (!advanceId) {
      toast.error(t('vendorAdvances.invalidAdvanceId'))
      return
    }
    navigate(`/purchases/vendor-advances/${advanceId}/edit`)
  }

  const handleDeleteAdvance = async (advance: VendorAdvance) => {
    const advanceId = advance.id
    if (!advanceId) {
      toast.error(t('vendorAdvances.invalidAdvanceId'))
      return
    }

    if (!window.confirm(t('vendorAdvances.confirmDelete'))) {
      return
    }

    try {
      // Note: Delete endpoint not provided in API, so this is a placeholder
      toast.error(t('vendorAdvances.deleteFunctionalityNotAvailable'))
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('vendorAdvances.failedToDeleteAdvance'))
    }
  }

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setPageNumber(1)
  }

  const handleVendorFilterChange = (value: string) => {
    if (value === 'all') {
      setSelectedVendorId(undefined)
    } else {
      setSelectedVendorId(Number(value))
    }
    setPageNumber(1)
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title={t('navigation.vendorAdvances')}
          description={t('vendorAdvances.manageAdvances')}
        />
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title={t('vendorAdvances.branchNotSelected')}
              description={t('vendorAdvances.selectBranchToViewAdvances')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('navigation.vendorAdvances')}
        description={t('vendorAdvances.manageAdvances')}
        action={{
          label: t('vendorAdvances.addNew'),
          onClick: handleAddAdvance,
          icon: Plus,
        }}
      />

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">{t('vendorAdvances.vendor')}</label>
              <Select value={selectedVendorId ? String(selectedVendorId) : 'all'} onValueChange={handleVendorFilterChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t('vendorAdvances.all')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('vendorAdvances.all')}</SelectItem>
                  {vendors.map(vendor => (
                    <SelectItem key={vendor.vendorId} value={String(vendor.vendorId)}>
                      {vendor.vendorNameEn || vendor.vendorNameAr || `Vendor ${vendor.vendorId}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">{t('vendorAdvances.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('vendorAdvances.search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => void handleCopy()}>
                <Copy className="mr-2 h-4 w-4" />
                {t('vendorAdvances.copy', { defaultValue: 'Copy' })}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCsv}>
                <FileDown className="mr-2 h-4 w-4" />
                CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Advances Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">{t('common.loading')}...</p>
            </div>
          ) : filteredAdvances.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title={searchQuery || selectedVendorId ? t('vendorAdvances.noAdvancesFound') : t('vendorAdvances.noAdvancesYet')}
                description={
                  searchQuery || selectedVendorId
                    ? t('vendorAdvances.tryAdjustingSearch')
                    : t('vendorAdvances.getStarted')
                }
                action={
                  !searchQuery && !selectedVendorId
                    ? {
                        label: t('vendorAdvances.addNewAdvance'),
                        onClick: handleAddAdvance,
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('vendorAdvances.advNo')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('vendorAdvances.vendor')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('vendorAdvances.billRef')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('vendorAdvances.paymentRef')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('vendorAdvances.date')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('vendorAdvances.amount')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('vendorAdvances.edit')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('vendorAdvances.delete')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAdvances.map((advance) => {
                      const advanceId = advance.id
                      return (
                        <TableRow key={advanceId} className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors">
                          <TableCell className="px-4 py-2 text-sm text-center align-middle font-medium">
                            {advance.advNo || `ADV-${advanceId}`}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {advance.vendorName || advance.vendorNameEn || advance.vendorNameAr || 'N/A'}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {advance.billRef || '-'}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {advance.paymentRef || '-'}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {formatDate(advance.date)}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {formatAmount(advance.amount)}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-center align-middle">
                            <div className="flex justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditAdvance(advance)}
                                title={t('vendorAdvances.editAdvance')}
                                className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-2 text-center align-middle">
                            <div className="flex justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteAdvance(advance)}
                                title={t('vendorAdvances.deleteAdvance')}
                                className="h-7 w-7 p-0 hover:bg-destructive/10 text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="border-t px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground">
                  {t('common.showing')} {(pageNumber - 1) * pageSize + 1} {t('common.to')}{' '}
                  {Math.min(pageNumber * pageSize, totalCount)} {t('common.of')} {totalCount} {t('common.entries')}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">{t('common.pageSize')}:</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                    disabled={pageNumber === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    {t('common.previous')}
                  </Button>
                  <div className="text-sm">
                    {t('common.page')} {pageNumber} {t('common.of')} {totalPages || 1}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => (prev < totalPages ? prev + 1 : prev))}
                    disabled={pageNumber >= totalPages}
                  >
                    {t('common.next')}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

